/**
 * YDoc-safe cloning of ReactFlow `node.data`.
 *
 * `node.data` carries LIVE `Y.Map`/`Y.Array` instances for `wireType: map`/`array`
 * fields (e.g. `out_image` on character/location/object designers and image/video/
 * audio nodes). The reader (`readDataFields`) returns raw Y values so the render
 * hot path keeps referentially-stable objects. But `structuredClone` of a live
 * `Y.Map` throws `DataCloneError`, and storing a live `Y.Map` reference in an
 * undo/clipboard snapshot lets a later YDoc delete tombstone the snapshot's assets.
 *
 * At the (infrequent) duplicate / copy / paste / undo boundaries we therefore
 * deep-convert every Y value to plain JS — at ANY depth, not just top-level. A
 * field can be a plain array/object that *contains* live Y.Maps (e.g. resolved
 * multi-input port refs), so converting only top-level values left nested Y
 * objects in place and `structuredClone` then threw `DataCloneError`, crashing
 * the page on delete/undo. The recursive walk below produces a fully Y-free deep
 * copy, so no `structuredClone` is needed (and it can't throw). The render path
 * is left untouched, so there is no per-render perf cost.
 *
 * Regression context: Workplace #1531385821952049. Designer outputs flipped from
 * `wireType: json` (clone-safe strings) to `wireType: map` (live Y.Map) when media
 * asset history ports were canonicalized, which re-broke duplicate/copy-paste/undo.
 */
import * as Y from 'yjs'
import { createNadDebug } from './debugLog'

const nodeOpsLog = createNadDebug('dw:canvas:node-ops')

function _isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

function _isGeneratedYMapWrapper(
  value: unknown,
): value is { readonly _raw: Y.Map<unknown>; toJSON(): unknown } {
  return (
    value !== null &&
    typeof value === 'object' &&
    '_raw' in value &&
    (value as { _raw?: unknown })._raw instanceof Y.Map &&
    typeof (value as { toJSON?: unknown }).toJSON === 'function'
  )
}

/**
 * Recursively convert a value to plain, Y-free JS:
 *   - any Yjs type (`Y.Map`/`Y.Array`/…) -> `.toJSON()` (which deep-converts its subtree)
 *   - generated `YMapWrapper` / `YMapRecord` objects -> `.toJSON()` recursively
 *   - plain arrays / plain objects -> recurse into elements/entries (catches Y types
 *     nested inside plain containers)
 *   - everything else (primitives, File, Date, functions) -> passthrough
 * Plain arrays/objects are rebuilt, so the result is also a deep copy.
 */
function _ydocToPlain(value: unknown, onYType: () => void): unknown {
  if (value instanceof Y.AbstractType) {
    onYType()
    return _ydocToPlain(value.toJSON(), onYType)
  }
  if (_isGeneratedYMapWrapper(value)) {
    onYType()
    return _ydocToPlain(value.toJSON(), onYType)
  }
  if (Array.isArray(value)) {
    return value.map((item) => _ydocToPlain(item, onYType))
  }
  if (value !== null && typeof value === 'object' && _isPlainObject(value)) {
    const out: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value)) {
      out[key] = _ydocToPlain(entry, onYType)
    }
    return out
  }
  return value
}

/**
 * Return a Y-free deep clone of a single node's data record. Live Yjs values at
 * any depth are converted to plain JS, so the result is safe to `structuredClone`,
 * store in undo/clipboard snapshots, and write back into a fresh YDoc node.
 *
 * Generic over the record type so call sites keep their precise
 * `ReactFlowCanvasNodeData` typing while tests can pass a raw
 * raw records from generated reader tests.
 */
export function ydocSafeCloneNodeData<T extends object>(data: T): T {
  let yConversions = 0
  const plain = _ydocToPlain(data, () => {
    yConversions += 1
  })
  if (yConversions > 0) {
    nodeOpsLog({
      event: 'ydoc-safe-clone',
      feature: 'canvas:node-ops',
      yConversions,
    })
  }
  return plain as T
}

/**
 * Return shallow-copied nodes whose `data` is Y-free deep-cloned via
 * {@link ydocSafeCloneNodeData}. Used for clipboard and undo/redo snapshots so a
 * later YDoc deletion cannot tombstone the snapshot's assets.
 */
export function ydocSafeSnapshotNodes<N extends { data: object }>(
  nodes: readonly N[],
): N[] {
  return nodes.map((node) => ({ ...node, data: ydocSafeCloneNodeData(node.data) }))
}
